import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Alert, Button } from '@mui/material';
import { AppDispatch } from '../../store/store';
import useDebounce from '../../components/useDebounce';
import {
  getGroupAdminStudies,
  getGroupAdminStudyImages,
  type GroupAdminStudy,
  type Pagination,
} from '../../rtk-apis/groupAdmin/groupAdminApi';
import GroupAdminLayout from './GroupAdminLayout';
import {
  CommandBar,
  DateField,
  ClinicFilter,
  HeadBand,
  RowCard,
  Identity,
  Cell,
  StatusPill,
  ListState,
  ListPager,
  LiteImagesDialog,
  formatDate,
  formatName,
  statusColor,
} from './GroupAdminShared';

/* Studies across the account's clinics, from the PACS, matched by each clinic's
   AE title — the same studies each clinic's own worklist shows. Read-only. */

const COLUMNS = 'minmax(260px, 2fr) minmax(110px, 0.8fr) minmax(170px, 1.3fr) minmax(170px, 1.3fr) minmax(116px, 0.8fr) minmax(190px, 1fr)';
const MIN_WIDTH = 1100;
const EMPTY_FILTERS = { clinicId: '', patientId: '', dateFrom: '', dateTo: '' };

const GroupAdminStudies: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [moreOpen, setMoreOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [rows, setRows] = useState<GroupAdminStudy[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ total: 0, page: 1, limit: 25, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagesFor, setImagesFor] = useState<GroupAdminStudy | null>(null);

  const set = (patch: Partial<typeof EMPTY_FILTERS>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await dispatch(
        getGroupAdminStudies({ ...filters, patientName: debouncedSearch.trim(), page, limit: perPage })
      ).unwrap();
      setRows(res.data);
      setPagination(res.pagination);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Could not load studies');
    } finally {
      setLoading(false);
    }
  }, [dispatch, filters, debouncedSearch, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => setPage(1), [debouncedSearch]);

  const isFiltering = !!(search || Object.values(filters).some(Boolean));

  return (
    <GroupAdminLayout>
      <div className="csl crl">
        <CommandBar
          title="Studies"
          count={pagination.total}
          noun={pagination.total === 1 ? 'study' : 'studies'}
          search={{ value: search, placeholder: 'Search patient name…', onChange: setSearch }}
        />

        <div className="csl-filters">
          <ClinicFilter value={filters.clinicId} onChange={(clinicId) => set({ clinicId })} />
          <button className={`csl-more ${moreOpen ? 'is-open' : ''}`} onClick={() => setMoreOpen((o) => !o)}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 6h16M7 12h10M10 18h4" />
            </svg>
            More filters
          </button>
          {isFiltering && (
            <button
              className="csl-clear"
              onClick={() => {
                setSearch('');
                setFilters(EMPTY_FILTERS);
                setPage(1);
              }}
            >
              Clear all
            </button>
          )}
        </div>

        {moreOpen && (
          <div className="csl-more-panel">
            <label className="csl-field">
              <span>Patient ID</span>
              <input type="text" value={filters.patientId} onChange={(e) => set({ patientId: e.target.value })} />
            </label>
            <DateField label="Study from" value={filters.dateFrom} onChange={(dateFrom) => set({ dateFrom })} />
            <DateField label="Study to" value={filters.dateTo} onChange={(dateTo) => set({ dateTo })} />
          </div>
        )}

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {rows.length > 0 && (
          <HeadBand columns={COLUMNS} minWidth={MIN_WIDTH} labels={['Patient', 'Study date', 'Description', 'Clinic', 'Report', '']} />
        )}

        <div className="csl-list">
          <ListState
            loading={loading}
            empty={!rows.length}
            icon="🫀"
            title="No studies found"
            hint={isFiltering ? 'Try clearing filters' : 'Studies received by your clinics will appear here'}
          />
          {rows.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 'fit-content', flexShrink: 0 }}>
              {rows.map((s) => {
                const report = s.reports[0];
                return (
                  <RowCard key={s.studyInstanceUID} columns={COLUMNS} minWidth={MIN_WIDTH} stripe={statusColor(report?.status)}>
                    <Identity
                      name={formatName(s.patientName)}
                      initialsFrom={s.patientName}
                      sub={`${s.patientId ? `MRN ${s.patientId}` : '—'} · DOB ${formatDate(s.dob)}`}
                    />
                    <Cell sub={s.studyTime ? `${s.studyTime.slice(0, 2)}:${s.studyTime.slice(2, 4)}` : undefined}>{formatDate(s.studyDate)}</Cell>
                    <Cell sub={s.modalities.join(', ') || undefined}>{s.description || '—'}</Cell>
                    <Cell>{s.clinics.map((c) => c.name).filter(Boolean).join(', ') || '—'}</Cell>
                    <Box>
                      <StatusPill status={report?.status} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button size="small" variant="outlined" onClick={() => setImagesFor(s)}>
                        Images
                      </Button>
                      {report && (
                        <Button size="small" variant="contained" onClick={() => navigate(`/group-admin/reports/${report._id}`)}>
                          Report
                        </Button>
                      )}
                    </Box>
                  </RowCard>
                );
              })}
            </Box>
          )}
        </div>

        <ListPager
          pagination={pagination}
          onPage={setPage}
          onPerPage={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      </div>

      <LiteImagesDialog
        open={!!imagesFor}
        title={imagesFor ? `${formatName(imagesFor.patientName)} · ${formatDate(imagesFor.studyDate)}` : ''}
        load={async () => (await dispatch(getGroupAdminStudyImages(imagesFor!.studyInstanceUID)).unwrap()).instances}
        onClose={() => setImagesFor(null)}
      />
    </GroupAdminLayout>
  );
};

export default GroupAdminStudies;
